import { useIntl } from 'react-intl';
import { Id } from 'react-toastify';

import { SharedToastProps } from '~/eds/src/types/SharedToastProps';
import { EntityType } from '~/enums/EntityType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { SearchQuery } from '~/features/advanced-search/types';
import { Highlight } from '~/features/document-viewer/types/highlight';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { State as XRayState } from '~/redux/slices/x-ray/types';
import { Uuid } from '~/types/Uuid';

import { HIGHLIGHT_SOURCE_LIMIT } from '../constants';
import { PromptModelTestCase } from '../types/deprecated';
import {
  prepareGoldValueForTagSource,
  prepareGoldValueValueForTagAnswer,
} from '../utils/tagging';
import { testIsTestCaseEvaluating } from '../utils/testcase';

export const useGetDocumentCount = ({
  entityId,
  entityType,
  query,
}: {
  entityId: string | null;
  entityType: EntityType.DocumentAttachedData | EntityType.Field;
  query: SearchQuery;
}) => {
  const activeQuery = isActiveQuery(query);

  const {
    data: docsInScope = 0,
    isFetching: isLoadingDocsInScope,
    error: errorDocsInScope,
  } = api.endpoints.getDocsInScope.useQuery(
    {
      // @ts-expect-error skipped if null
      entityId: entityId
        ? entityId
        : entityType === EntityType.DocumentAttachedData
          ? ''
          : null,
      entityType,
      query: activeQuery,
    },
    { skip: !entityId && entityType === EntityType.Field },
  );

  return {
    docsInScope,
    isLoadingDocsInScope,
    errorDocsInScope,
  };
};

interface HighlightActionsHookProps {
  activeTestCase: PromptModelTestCase | undefined;
  config: XRayState['configuration']; // TODO: Update to PromptModelConfig
  disableEdit: boolean;
  isPreviousVersion: boolean;
  isTableModel: boolean;
  modelId: Uuid;
  onUpdateTestCaseHighlights: (
    testCase: PromptModelTestCase,
    highlight: Highlight,
    options?: { isNew?: boolean },
  ) => void;
  onToast: (props: SharedToastProps) => Id;
}

/** Cusotm AI Tagging hook */
export const useGetHighlightActions = ({
  activeTestCase,
  config,
  disableEdit,
  isPreviousVersion,
  isTableModel,
  modelId,
  onUpdateTestCaseHighlights,
  onToast,
}: HighlightActionsHookProps) => {
  const intl = useIntl();
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const { RETRY_ERROR } = useErrors();

  const [tagAnswer, { isLoading: isTaggingAnswer }] =
    api.endpoints.tagAnswerForPromptModelTestCase.useMutation();

  const [updateTestCase, { isLoading: isUpdatingPromptModelTestCase }] =
    api.endpoints.updatePromptModelTestCase.useMutation();

  const handleDeleteSource = (highlight: Highlight) => {
    if (!activeTestCase) {
      return;
    }
    const { goldValue, modelValue, outcome } = activeTestCase;

    const sourceHighlights =
      goldValue?.highlights ?? modelValue?.highlights ?? [];

    updateTestCase({
      modelId,
      version: activeTestCase?.versionNumber,
      highlightMethod,
      testCaseNumber: activeTestCase?.number,
      outcome,
      goldValue: {
        highlights: sourceHighlights.filter((h) => h.id !== highlight.id),
        type:
          outcome === 'correct' || goldValue?.value
            ? modelValue?.type
            : modelValue?.type === 'classification'
              ? 'classification-unknown'
              : 'unknown',
        // @ts-expect-error - 'string[]' is not assignable to 'string'
        value: goldValue?.value ?? null,
      },
    })
      .unwrap()
      .then((updatedTestCase) => {
        if (updatedTestCase) {
          onToast({
            message: intl.formatMessage({
              id: 'ModelDetailsHooks.sourceDeleted',
              defaultMessage: 'Source deleted.',
              description: 'Success message when a source is deleted',
            }),
            status: 'success',
          });
          onUpdateTestCaseHighlights(updatedTestCase, highlight);
        }
      })
      .catch(() => onToast({ message: RETRY_ERROR, status: 'danger' }));
  };

  const handleTagAnswer = (highlight: Highlight) => {
    if (!activeTestCase || !highlight.text) {
      return;
    }
    const { goldValue, modelValue, outcome } = activeTestCase;

    tagAnswer({
      modelId,
      version: activeTestCase?.versionNumber,
      testCaseNumber: activeTestCase?.number,
      outcome,
      fallbackGoldValue: prepareGoldValueForTagSource({
        goldValue,
        modelValue,
        outcome,
        highlight,
      }),
      goldValue: {
        highlights: [
          ...(goldValue?.highlights ?? modelValue?.highlights ?? []),
          highlight,
        ],
        type: modelValue?.type,
        // @ts-expect-error - 'string[]' is not assignable to 'string'
        value: prepareGoldValueValueForTagAnswer({
          value: highlight.text,
          goldValue,
          modelValue,
          config,
        }),
      },
    })
      .unwrap()
      .then((response) => {
        if (!response) {
          return;
        }
        const { updatedTestCase, responseToast } = response;
        if (responseToast) {
          onToast(responseToast);
        }
        if (updatedTestCase) {
          onUpdateTestCaseHighlights(updatedTestCase, highlight, {
            isNew: true,
          });
        }
      })
      .catch(({ responseToast }) =>
        onToast(responseToast ?? { message: RETRY_ERROR, status: 'danger' }),
      );
  };

  const handleTagSource = (highlight: Highlight) => {
    if (!activeTestCase) {
      return;
    }
    const { goldValue, modelValue, outcome } = activeTestCase;
    updateTestCase({
      modelId,
      version: activeTestCase?.versionNumber,
      highlightMethod,
      testCaseNumber: activeTestCase?.number,
      outcome,
      goldValue: prepareGoldValueForTagSource({
        goldValue,
        modelValue,
        outcome,
        highlight,
      }),
    })
      .unwrap()
      .then((updatedTestCase) => {
        onToast({
          message: intl.formatMessage({
            id: 'ModelDetailsHooks.sourceAdded',
            defaultMessage: 'Source added.',
            description: 'Success message when a source is added',
          }),
          status: 'success',
        });
        if (updatedTestCase) {
          onUpdateTestCaseHighlights(updatedTestCase, highlight, {
            isNew: true,
          });
        }
      })
      .catch(() => onToast({ message: RETRY_ERROR, status: 'danger' }));
  };

  // Custom AI Tagging disabled logic
  const sourceLimitReached =
    (activeTestCase?.goldValue?.highlights?.length ?? 0) >=
    HIGHLIGHT_SOURCE_LIMIT;

  const isTestCaseEvaluating =
    !activeTestCase || testIsTestCaseEvaluating(activeTestCase);

  const disableDeleteSource = isTableModel || disableEdit || isPreviousVersion;

  const disableDeleteSourceTooltip = isTableModel
    ? intl.formatMessage({
        id: 'ModelDetailsHooks.deleteSourcesDisabledForTableModels',
        defaultMessage: 'Deleting sources is disabled for table models.',
        description:
          'Tooltip explaining why tagging answers is disabled for dropdown models',
      })
    : disableEdit
      ? intl.formatMessage({
          id: 'ModelDetailsHooks.modelPublishedDeleteSource',
          defaultMessage:
            'Model is already published. Edit Model to delete sources.',
          description:
            'Tooltip when trying to delete sources from a published model',
        })
      : isPreviousVersion
        ? intl.formatMessage({
            id: 'ModelDetailsHooks.deleteSourceLatestVersion',
            defaultMessage:
              'You can only delete sources from the latest version.',
            description:
              'Tooltip when trying to delete sources from a previous version',
          })
        : undefined;

  const disableTagAnswer =
    isTableModel || disableEdit || isPreviousVersion || isTestCaseEvaluating;

  const isCloseEndedDropdown =
    config.fieldClassification && !config.fieldClassification.isOpenEnded;

  const tagSourceDisabledTooltip = intl.formatMessage({
    id: 'ModelDetailsHooks.modelPublishedTagSources',
    defaultMessage: 'Model is already published. Edit Model to tag sources.',
    description: 'Tooltip when trying to tag sources in a published model',
  });

  const tagSourceOnLatestVersionTooltip = intl.formatMessage({
    id: 'ModelDetailsHooks.tagSourcesLatestVersion',
    defaultMessage: 'You can only tag sources in the latest version.',
    description: 'Tooltip when trying to tag sources in a previous version',
  });

  const evaluateModelTooltip = intl.formatMessage({
    id: 'ModelDetailsHooks.evaluatingModel',
    defaultMessage: 'Evaluating model…',
    description: 'Tooltip when model is currently being evaluated',
  });

  const disableTagAnswerTooltip = isTableModel
    ? intl.formatMessage({
        id: 'ModelDetailsHooks.taggingAnswersDisabledForTableModels',
        defaultMessage: 'Tagging answers is disabled for table models.',
        description:
          'Tooltip explaining why tagging answers is disabled for table models',
      })
    : isCloseEndedDropdown
      ? intl.formatMessage({
          id: 'ModelDetailsHooks.taggingAnswersDisabled',
          defaultMessage:
            'Tagging answers is disabled for dropdown models with custom classification options. Provide the correct answer after rating the evaluation Incorrect, or configure the model to allow AI-created options.',
          description:
            'Tooltip explaining why tagging answers is disabled for dropdown models',
        })
      : disableEdit
        ? tagSourceDisabledTooltip
        : isPreviousVersion
          ? tagSourceOnLatestVersionTooltip
          : isTestCaseEvaluating
            ? evaluateModelTooltip
            : undefined;

  const disableTagSource =
    isTableModel || disableTagAnswer || sourceLimitReached;

  const disableTagSourceTooltip = isTableModel
    ? intl.formatMessage({
        id: 'ModelDetailsHooks.taggingSourcesDisabledForTableModels',
        defaultMessage: 'Tagging sources is disabled for table models.',
        description:
          'Tooltip explaining why tagging sources is disabled for table models',
      })
    : disableEdit
      ? tagSourceDisabledTooltip
      : isPreviousVersion
        ? tagSourceOnLatestVersionTooltip
        : isTestCaseEvaluating
          ? evaluateModelTooltip
          : sourceLimitReached
            ? intl.formatMessage({
                id: 'ModelDetailsHooks.maxSourcesReached',
                defaultMessage:
                  'You have reached the maximum number of sources.',
                description:
                  'Tooltip when maximum number of sources is reached',
              })
            : undefined;

  /**
   * Custom AI Tagging popover action on existing highlights.
   */
  const getHighlightClickActions = (highlight: Highlight) => [
    {
      disabled: disableDeleteSource,
      label: intl.formatMessage({
        id: 'ModelDetailsHooks.deleteSource',
        defaultMessage: 'Delete Source',
        description: 'Label for delete source action',
      }),
      leftIcon: 'trash' as const,
      tooltip: disableDeleteSourceTooltip,
      value: 'delete',
      onClick: () => handleDeleteSource(highlight),
    },
  ];

  /**
   * Custom AI Tagging popover actions on new highlights.
   */
  const getHighlightCreateActions = (highlight: Highlight) => [
    {
      disabled: disableTagAnswer || isCloseEndedDropdown,
      isLoading: isTaggingAnswer,
      label: intl.formatMessage({
        id: 'ModelDetailsHooks.tagAsAnswer',
        defaultMessage: 'Tag as Answer',
        description: 'Label for tag as answer action',
      }),
      leftIcon: 'field' as const,
      tooltip: disableTagAnswerTooltip,
      value: 'answer',
      onClick: () => handleTagAnswer(highlight),
    },
    {
      disabled: disableTagSource,
      isLoading: isUpdatingPromptModelTestCase,
      label: intl.formatMessage({
        id: 'ModelDetailsHooks.tagAsSource',
        defaultMessage: 'Tag as Source',
        description: 'Label for tag as source action',
      }),
      leftIcon: 'tag' as const,
      tooltip: disableTagSourceTooltip,
      value: 'source',
      onClick: () => handleTagSource(highlight),
    },
  ];

  return {
    getHighlightClickActions,
    getHighlightCreateActions,
  };
};
